import {
  ArrowCounterClockwiseIcon,
  FileTextIcon,
  FolderOpenIcon,
  GitBranchIcon,
  GithubLogoIcon,
  MoonIcon,
  SunIcon,
  TestTubeIcon,
  TextboxIcon,
  TranslateIcon,
} from '@phosphor-icons/react';
import { useRef } from 'react';
import type { Locale } from '../i18n';
import { useI18n } from '../i18n';
import type { Theme } from '../app/theme';

interface HeaderProps {
  readonly locale: Locale;
  readonly theme: Theme;
  readonly folderSupported: boolean;
  readonly onLocaleChange: (locale: Locale) => void;
  readonly onThemeChange: () => void;
  readonly onFolderPicked: (files: readonly File[]) => void;
  readonly onGitignorePicked: (file: File) => void;
  readonly onOpenPaste: () => void;
  readonly onLoadSample: () => void;
  readonly onReset: () => void;
}

const directoryInputAttributes = {
  directory: '',
  webkitdirectory: '',
};

export function Header({
  locale,
  theme,
  folderSupported,
  onLocaleChange,
  onThemeChange,
  onFolderPicked,
  onGitignorePicked,
  onOpenPaste,
  onLoadSample,
  onReset,
}: HeaderProps) {
  const { t } = useI18n();
  const folderInput = useRef<HTMLInputElement>(null);
  const gitignoreInput = useRef<HTMLInputElement>(null);

  return (
    <header className="app-header">
      <a className="brand" href="./" aria-label={t('brand.home')}>
        <span className="brand-mark" aria-hidden="true">
          <GitBranchIcon />
        </span>
        <span className="brand-copy">
          <strong>WhatTheIgnore</strong>
          <small>{t('brand.tagline')}</small>
        </span>
      </a>

      <nav className="action-bar" aria-label={t('header.inputActions')}>
        <span id="folder-support-note" className="visually-hidden">
          {t('input.folderUnsupported')}
        </span>
        <input
          {...directoryInputAttributes}
          ref={folderInput}
          className="visually-hidden"
          type="file"
          multiple
          onChange={(event) => {
            const files = Array.from(event.currentTarget.files ?? []);
            if (files.length > 0) onFolderPicked(files);
            event.currentTarget.value = '';
          }}
        />
        <input
          ref={gitignoreInput}
          className="visually-hidden"
          type="file"
          accept=".gitignore,text/plain"
          onChange={(event) => {
            const file = event.currentTarget.files?.item(0);
            if (file) onGitignorePicked(file);
            event.currentTarget.value = '';
          }}
        />

        <button
          className="button button-primary"
          type="button"
          disabled={!folderSupported}
          aria-describedby={!folderSupported ? 'folder-support-note' : undefined}
          onClick={() => {
            folderInput.current?.click();
          }}
        >
          <FolderOpenIcon aria-hidden="true" />
          <span>{t('actions.loadFolder')}</span>
        </button>
        <button className="button" type="button" onClick={() => gitignoreInput.current?.click()}>
          <FileTextIcon aria-hidden="true" />
          <span>{t('actions.loadGitignore')}</span>
        </button>
        <button className="button" type="button" onClick={onOpenPaste}>
          <TextboxIcon aria-hidden="true" />
          <span>{t('actions.pastePaths')}</span>
        </button>
        <button className="button" type="button" onClick={onLoadSample}>
          <TestTubeIcon aria-hidden="true" />
          <span>{t('actions.loadSample')}</span>
        </button>
        <button className="button button-quiet" type="button" onClick={onReset}>
          <ArrowCounterClockwiseIcon aria-hidden="true" />
          <span>{t('actions.reset')}</span>
        </button>
      </nav>

      <div className="header-tools">
        <label className="compact-select">
          <TranslateIcon aria-hidden="true" />
          <span className="visually-hidden">{t('actions.language')}</span>
          <select
            aria-label={t('actions.language')}
            value={locale}
            onChange={(event) => {
              onLocaleChange(event.currentTarget.value as Locale);
            }}
          >
            <option value="en">English</option>
            <option value="zh-CN">简体中文</option>
          </select>
        </label>
        <button
          className="icon-button"
          type="button"
          aria-label={theme === 'dark' ? t('actions.useLightTheme') : t('actions.useDarkTheme')}
          onClick={onThemeChange}
        >
          {theme === 'dark' ? <SunIcon aria-hidden="true" /> : <MoonIcon aria-hidden="true" />}
        </button>
        <a
          className="icon-button"
          href="https://github.com/KanadeK/what-the-ignore"
          target="_blank"
          rel="noreferrer"
          aria-label={t('actions.openGithub')}
        >
          <GithubLogoIcon aria-hidden="true" />
        </a>
      </div>
    </header>
  );
}
